import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {D} from '../ai-chat/design-tokens';
import {graphql, useFragment} from 'react-relay';
import {withRouter, RouteComponentProps} from 'react-router-dom';
import {useAgentDrawerContext} from '../../forecast-app/shared/contexts/AgentDrawerContext';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import {canUseAgentChat} from './agentChatGate';
import {isGenAiDisabled} from '../../forecast-app/shared/util/FeatureUtil';
import DrawerHeader from './DrawerHeader';
import DrawerBody from './DrawerBody';
import ChatPanel from './ChatPanel';
import {PromptDropdown_viewer$key} from './__generated__/PromptDropdown_viewer.graphql';
import {PromptDropdown_viewerFragment} from './PromptDropdown';

const DRAWER_WIDTH = 480;

const DrawerContainer = styled.div<{isOpen: boolean}>`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: ${DRAWER_WIDTH}px;
	transform: ${({isOpen}) => (isOpen ? 'translateX(0)' : `translateX(${DRAWER_WIDTH}px)`)};
	transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
	border-left: 1px solid ${D.inputBorder};
	box-shadow: ${D.drawerShadow};
	background: ${D.bg};
	display: flex;
	flex-direction: column;
	z-index: 100;
`;

const AgentDrawerQuery = graphql`
	query AgentDrawerQuery {
		viewer {
			actualPersonId
			backendId
			company {
				id
				name
			}
			profiles(first: 1) {
				edges {
					node {
						seat
					}
				}
			}
			...PromptDropdown_viewer
		}
	}
`;

type DrawerContentProps = {
	viewer: {
		backendId: number;
		company: {
			id: string;
			name: string;
		};
		profiles: {edges: Array<{node: {seat: string | null} | null} | null> | null} | null;
	} & PromptDropdown_viewer$key;
	history: RouteComponentProps['history'];
	onClose: () => void;
};

const DrawerContent = ({viewer, history, onClose}: DrawerContentProps): JSX.Element => {
	const {setSavedPromptTexts} = useAgentDrawerContext();
	const promptData = useFragment(PromptDropdown_viewerFragment, viewer);
	const savedTexts = useMemo(() => {
		const edges = promptData?.savedPrompts?.edges ?? [];
		return new Set(edges.map(e => e?.node?.promptText ?? '').filter(Boolean)) as ReadonlySet<string>;
	}, [promptData]);
	useEffect(() => {
		setSavedPromptTexts(savedTexts);
	}, [savedTexts, setSavedPromptTexts]);

	const [hasLlmResponse, setHasLlmResponse] = useState(false);
	const clearRef = useRef<(() => void) | null>(null);
	const regenerateRef = useRef<(() => void) | null>(null);
	const registerClear = useCallback((fn: () => void) => {
		clearRef.current = fn;
	}, []);
	const registerRegenerate = useCallback((fn: () => void) => {
		regenerateRef.current = fn;
	}, []);
	const handleClearChat = useCallback(() => {
		clearRef.current?.();
	}, []);
	const handleRegenerateLastReply = useCallback(() => {
		regenerateRef.current?.();
	}, []);

	const companyId = viewer.company.id;
	const companyName = viewer.company.name;

	return (
		<>
			<DrawerHeader
				onClose={onClose}
				onClearChat={handleClearChat}
				onRegenerateLastReply={handleRegenerateLastReply}
				canRegenerate={hasLlmResponse}
				viewerRef={viewer}
			/>
			<DrawerBody>
				<ChatPanel
					viewerPersonId={viewer.backendId}
					companyId={companyId}
					companyName={companyName}
					history={history}
					onRegisterClear={registerClear}
					onRegisterRegenerate={registerRegenerate}
					onHasLlmResponseChange={setHasLlmResponse}
				/>
			</DrawerBody>
		</>
	);
};

const AgentDrawerInner = ({history}: RouteComponentProps): JSX.Element => {
	const {isOpen, closeDrawer} = useAgentDrawerContext();
	const genAiDisabled = isGenAiDisabled();

	// Shut the drawer if the switch is thrown while it is open, rather than leaving a live chat
	// on screen that the gateway will now refuse.
	useEffect(() => {
		if (isOpen && genAiDisabled) closeDrawer();
	}, [isOpen, genAiDisabled, closeDrawer]);

	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closeDrawer();
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [isOpen, closeDrawer]);

	return (
		<DrawerContainer isOpen={isOpen} data-cy="agent-drawer">
			<ForecastQueryRenderer
				key="agent-drawer-query"
				query={AgentDrawerQuery}
				showLoader={false}
				render={(relayProps: {viewer: DrawerContentProps['viewer']} | null) => {
					if (!relayProps?.viewer) return null;
					// The gate lives here, not only on the nav trigger: the router mounts this drawer
					// for every logged-in user, and the report-builder "Edit Prompt" banner opens it
					// through a gate of its own that excludes client users but not collaborator seats.
					if (!canUseAgentChat(relayProps.viewer.profiles)) return null;
					// The kill switch is enforced here too, and not only by greying the nav trigger:
					// the report-builder "Edit Prompt" banner opens the drawer without going near
					// that button.
					if (isGenAiDisabled()) return null;
					return <DrawerContent viewer={relayProps.viewer} history={history} onClose={closeDrawer} />;
				}}
			/>
		</DrawerContainer>
	);
};

export default withRouter(AgentDrawerInner);
